import { Alert, Box, Chip, CircularProgress, List, ListItemButton, Pagination, Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ESTADOS_ABIERTOS, getEstadoTramiteColor, getEstadoTramiteLabel, TAMANO_PAGINA, type FilaBandeja } from '../../types/tramiteGremio';
import { formatCuit, formatDate } from '../../utils/format';

interface Props {
  filas: FilaBandeja[];
  total: number;
  pagina: number;
  seleccionado?: string;
  cargando: boolean;
  error?: Error | null;
  onSeleccionar: (id: string) => void;
  onPagina: (p: number) => void;
}

/** Pasados estos días sin movimiento, un trámite abierto se marca como estancado. */
export const DIAS_ESTANCADO = 7;

export default function BandejaLista({ filas, total, pagina, seleccionado, cargando, error, onSeleccionar, onPagina }: Props) {
  const paginas = Math.max(1, Math.ceil(total / TAMANO_PAGINA));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 0.5, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" role="status">
          {cargando && filas.length === 0 ? 'Buscando…' : `${total} ${total === 1 ? 'trámite' : 'trámites'}`}
        </Typography>
        {cargando && filas.length > 0 && <CircularProgress size={16} aria-label="Actualizando" />}
      </Stack>

      {error && <Alert severity="error">No pudimos cargar los trámites: {error.message}</Alert>}
      {!error && !cargando && filas.length === 0 && (
        <Alert severity="info">No hay trámites con estos filtros.</Alert>
      )}

      <List disablePadding aria-label="Trámites" sx={{ border: filas.length ? 1 : 0, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        {filas.map((f) => {
          const abierto = ESTADOS_ABIERTOS.includes(f.estado);
          const estancado = abierto && f.dias_sin_movimiento >= DIAS_ESTANCADO;
          return (
            <ListItemButton key={f.id} selected={f.id === seleccionado} onClick={() => onSeleccionar(f.id)} divider sx={{ alignItems: 'flex-start', py: 1.25 }}>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                  <Typography fontWeight={700} noWrap>
                    {f.afiliado}
                  </Typography>
                  <Chip size="small" label={getEstadoTramiteLabel(f.estado)} color={getEstadoTramiteColor(f.estado)} sx={{ flexShrink: 0 }} />
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {f.codigo} · {f.tipo} · {formatCuit(f.cuil)}
                </Typography>
                <Typography variant="body2" noWrap sx={{ mt: 0.25 }}>
                  {f.resumen}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(f.created_at)}
                  </Typography>
                  {f.asignado_nombre ? (
                    <Chip size="small" variant="outlined" label={f.asignado_nombre} />
                  ) : abierto ? (
                    <Chip size="small" color="warning" variant="outlined" label="Sin asignar" />
                  ) : null}
                  {estancado && <Chip size="small" color="error" variant="outlined" label={`${f.dias_sin_movimiento} días sin movimiento`} />}
                  {f.adjuntos > 0 && (
                    <Stack direction="row" alignItems="center" spacing={0.25} aria-label={`${f.adjuntos} adjuntos`}>
                      <AttachFileIcon sx={{ fontSize: 16 }} color="action" />
                      <Typography variant="caption">{f.adjuntos}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </ListItemButton>
          );
        })}
      </List>

      {paginas > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination count={paginas} page={pagina} onChange={(_, p) => onPagina(p)} size="small" siblingCount={0} />
        </Stack>
      )}
    </Box>
  );
}
